import React, { useMemo } from 'react'
import { Check, MoreVertical } from 'lucide-react'

import { BookmarkIndicator } from '@/components/BookmarkIndicator'
import { Separator } from '@/components/ui'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { canDeleteFiles } from '@/features/deliverable-set'
import {
  useAddBookmark,
  useDocumentStore,
  useRemoveBookmark,
} from '@/features/document'
import { canDeleteDocument } from '@/features/view-tfl-uploaded/utils/validation.ts'
import { ResponseDeliverableSet, ResponseDocumentListItem } from '@/schema'
import { cn } from '@/utils'

import { useTflViewerStore } from '../store'
import { getStatusIcon, getStatusLabel } from '../utils/statusIcons'

interface TFLFileListItemProps {
  readonly item: ResponseDocumentListItem
  readonly isActive: boolean
  readonly onClick: () => void
  readonly onDeleteSingle: () => void
  readonly onDeleteMulti: () => void
  readonly onSelectDelete: () => void
  readonly onDeselectDelete: () => void
  readonly deliverableSet?: ResponseDeliverableSet | null
}

export function TFLFileListItem({
  item,
  isActive,
  onClick,
  onDeleteSingle,
  onDeleteMulti,
  onSelectDelete,
  onDeselectDelete,
  deliverableSet,
}: Readonly<TFLFileListItemProps>) {
  const { isDeleteMultiple, deleteMultipleDocumentIds } = useDocumentStore()
  const { setDocumentMetadata, metadataByDocId } = useTflViewerStore()
  const { mutate: addBookmark } = useAddBookmark()
  const { mutate: removeBookmark } = useRemoveBookmark()

  const canDelete =
    canDeleteDocument(item.status) &&
    (deliverableSet ? canDeleteFiles(deliverableSet) : true)

  const isDeleteSelected = useMemo(
    () => deleteMultipleDocumentIds.includes(item.document_id),
    [deleteMultipleDocumentIds, item.document_id]
  )

  const isBookmarked =
    metadataByDocId[item.document_id]?.is_bookmarked ?? item.is_bookmarked

  const handleSelectDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()

    if (isDeleteSelected) onDeselectDelete()
    else onSelectDelete()
  }

  const handleAddBookmark = () => {
    addBookmark(item.document_id, {
      onSuccess: () => {
        setDocumentMetadata(item.document_id, { is_bookmarked: true })
      },
    })
  }

  const handleRemoveBookmark = () => {
    removeBookmark(item.document_id, {
      onSuccess: () => {
        setDocumentMetadata(item.document_id, { is_bookmarked: false })
      },
    })
  }

  const bookmarkMenuItem = useMemo(() => {
    const onSelect = isBookmarked ? handleRemoveBookmark : handleAddBookmark
    const label = isBookmarked ? 'Remove bookmark' : 'Add bookmark'

    return (
      <DropdownMenuItem
        onSelect={onSelect}
        className={cn(
          'text-primary-900 font-medium text-md hover:bg-primary-hover',
          'py-px px-sm rounded-sm'
        )}
      >
        <div className="text-sm font-medium p-md">{label}</div>
      </DropdownMenuItem>
    )
  }, [isBookmarked]) // handlers are stable enough for Sonar; if lint complains, include them too

  const deleteMenuItems = useMemo(() => {
    if (!canDelete) return null

    return (
      <>
        <DropdownMenuItem
          onSelect={onDeleteSingle}
          className={cn(
            'text-primary-900 font-medium text-md hover:bg-primary-hover',
            'py-px px-sm rounded-sm'
          )}
        >
          <div className="text-sm font-medium p-md">Delete file</div>
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={onDeleteMulti}
          className={cn(
            'text-primary-900 font-medium text-md hover:bg-primary-hover',
            'py-px px-sm rounded-sm'
          )}
        >
          <div className="text-sm font-medium p-md">Delete multiple files</div>
        </DropdownMenuItem>

        {/* apply negative margins to counteract dropdown margin*/}
        <Separator className="w-[calc(100%+theme(spacing.xs)*2)] -mx-xs" />
      </>
    )
  }, [canDelete, onDeleteMulti, onDeleteSingle])

  const actionNode = useMemo(() => {
    if (isDeleteMultiple) {
      if (!canDelete) return null

      return (
        <button
          type="button"
          className="flex items-center gap-2 p-1 bg-transparent border-none cursor-pointer"
          onClick={handleSelectDelete}
          aria-label={
            isDeleteSelected ? 'Deselect for deletion' : 'Select for deletion'
          }
          aria-pressed={isDeleteSelected}
        >
          <div
            className={cn(
              'size-4 rounded-xs flex items-center justify-center',
              'border border-solid border-primary',
              isDeleteSelected
                ? 'bg-brand-solid border-transparent'
                : 'bg-transparent'
            )}
          >
            {isDeleteSelected ? (
              <Check size={12} className="text-white" />
            ) : null}
          </div>
        </button>
      )
    }

    return (
      <DropdownMenu>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="text"
                className={cn(
                  'flex-shrink-0 h-[22px] w-[22px] p-0',
                  'rounded-2xl hover:bg-primary-hover',
                  isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-[16px] w-[16px] text-quaternary-500" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent className="z-40">
            <span>More options</span>
          </TooltipContent>
        </Tooltip>

        <DropdownMenuContent
          className="sm:w-50 sm:min-w-none rounded-md py-xs border border-solid border-secondary-alt shadow-lg"
          align="end"
          onClick={(e) => e.stopPropagation()}
        >
          {deleteMenuItems}
          {bookmarkMenuItem}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }, [
    canDelete,
    deleteMenuItems,
    bookmarkMenuItem,
    handleSelectDelete,
    isActive,
    isDeleteMultiple,
    isDeleteSelected,
  ])

  const bookmarkIcon = useMemo(() => {
    if (!isBookmarked) return null
    return <BookmarkIndicator />
  }, [isBookmarked])

  return (
    <button
      type="button"
      className={cn(
        'group flex justify-items-center items-start w-full text-left',
        'gap-2 pr-2 pl-4 py-3',
        'cursor-pointer transition-colors bg-transparent border-none',
        isActive ? 'bg-tertiary' : 'hover:bg-primary-hover'
      )}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-center rounded-sm size-6 bg-quaternary">
              {getStatusIcon(item.status)}
            </div>
          </TooltipTrigger>
          <TooltipContent side="right">
            <span>{getStatusLabel(item.status)}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <div className="flex items-center flex-1 min-w-0">
        <span
          className={cn(
            'text-sm font-normal text-secondary-700 break-all',
            isActive && 'font-medium'
          )}
          title={item.display_name}
        >
          {item.display_name}
        </span>
      </div>

      {actionNode}
      {bookmarkIcon}
    </button>
  )
}
